import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@ooh/ui/components/card';
import { FieldRow } from '@ooh/ui/components/field-row';
import { Separator } from '@ooh/ui/components/separator';
import { formatPrice } from '@ooh/ui/lib/media-unit-formatters';
import { computePriceBreakdown } from './lib/price-breakdown';
import type { DetailViewUnit } from './detail-view-unit';

const isVisible = (item: { value: number | null }) => item.value !== null;

/**
 * "Ceny" (client-facing price) breakdown — rendered identically on both the
 * internal `/media-units/$id` route (canViewCosts=true) and the client
 * `/offers/$offerId/units/$unitId` route (canViewCosts=false). Every field it
 * reads is already part of `ClientMediaUnitDetailSchema`, so this card is NOT
 * gated by `canViewCosts` — it renders unconditionally on both payloads.
 *
 * Distinct from and untouched by the internal-only "Koszty" card below it
 * (rent, exposureCost, installCost, dismantlingCost, productionCost,
 * additionalCosts — no catalogPrice, no printCost), which stays gated by
 * canViewCosts.
 *
 * See CU-86cb1p3ag for the confirmed price-line classification (which
 * components sum into "Suma" vs. render separately as an additional,
 * non-summed service).
 */
export function PriceBreakdownCard({ unit }: { unit: DetailViewUnit }) {
  const { t } = useTranslation('inventory');
  const priceBreakdown = computePriceBreakdown(unit);
  // Only render line items that actually have a value — a dash row for a fee
  // that simply doesn't apply to this unit (e.g. no mural production, no
  // dismantling) reads as broken/noisy rather than informative.
  const visibleCoreLineItems = priceBreakdown.coreLineItems.filter(isVisible);
  const visibleAdditionalLineItems = priceBreakdown.additionalLineItems.filter(isVisible);

  return (
    <Card data-testid="price-status-card">
      <CardContent className="pt-5 pb-4">
        {priceBreakdown.showStarterAsReference ? (
          <>
            <div className="mb-1 grid grid-cols-2 divide-x divide-border/60">
              <div className="pr-4" data-testid="field-starterPrice">
                <div className="text-xs text-muted-foreground">
                  {t('detail.fields.starterPrice')}
                </div>
                <div className="text-lg font-semibold">
                  {formatPrice(priceBreakdown.starterPrice)}
                </div>
              </div>
              <div className="pl-4" data-testid="field-priceHeadline">
                <div className="text-xs font-medium text-emerald-600 dark:text-emerald-500">
                  {t('detail.fields.discountedPrice')}
                </div>
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-500">
                  {formatPrice(priceBreakdown.headlinePrice)}
                </div>
              </div>
            </div>
            <div className="text-right text-xs text-muted-foreground">{t('detail.perMonth')}</div>
          </>
        ) : (
          <>
            <div
              className="mb-1 text-right text-2xl font-bold text-emerald-600 dark:text-emerald-500"
              data-testid="field-priceHeadline"
            >
              {formatPrice(priceBreakdown.headlinePrice)}
            </div>
            <div className="text-right text-xs text-muted-foreground">{t('detail.perMonth')}</div>
          </>
        )}
        <Separator className="my-4" />
        <div>
          {visibleCoreLineItems.map((item) => (
            <FieldRow
              key={item.key}
              testId={`field-${item.key}`}
              label={t(`detail.fields.${item.key}`)}
            >
              <span className="block font-mono text-xs text-right">{formatPrice(item.value)}</span>
            </FieldRow>
          ))}
          <FieldRow
            testId="field-priceSum"
            label={t('detail.fields.priceSum')}
            className="border-t border-border/60 pt-2 font-bold"
          >
            <span className="block font-mono text-xs font-bold text-right">
              {formatPrice(priceBreakdown.sum)}
            </span>
          </FieldRow>
        </div>
        {visibleAdditionalLineItems.length > 0 ? (
          <div
            className="mt-4 border-t border-border/60 pt-4"
            data-testid="additional-services-section"
          >
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('detail.sections.additionalServices')}
            </h3>
            <div>
              {visibleAdditionalLineItems.map((item) => (
                <FieldRow
                  key={item.key}
                  testId={`field-${item.key}`}
                  label={t(`detail.fields.${item.key}`)}
                >
                  <span className="block font-mono text-xs text-right">
                    {formatPrice(item.value)}
                  </span>
                </FieldRow>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
